import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import './Learn.css';

/**
 * Learn Page Component
 * Visual guide to help users understand how to use Mia Flow
 */
function Learn() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = [
    {
      title: 'Welcome to Mia Flow',
      description: 'Your AI-powered development platform that turns thoughts into code',
      icon: (
        <svg viewBox="0 0 100 100" className="step-icon">
          <path
            d="M50 10 L90 30 L90 70 L50 90 L10 70 L10 30 Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          />
          <circle cx="50" cy="50" fill="currentColor" r="15" />
        </svg>
      ),
      image: '/login-background.png',
      content: (
        <div className="step-content">
          <h3>{'What is Mia Flow?'}</h3>
          <p>
            Mia Flow is an enterprise-grade AI coding assistant that helps you build, modernize, 
            and deploy applications faster. Whether you're starting a new project or modernizing 
            legacy code, Mia Flow is here to help.
          </p>
          <div className="feature-grid">
            <div className="feature-card">
              <svg className="feature-icon-small" viewBox="0 0 20 20">
                <path
                  d="M10 2 L12 8 L18 8 L13 12 L15 18 L10 14 L5 18 L7 12 L2 8 L8 8 Z"
                  fill="currentColor"
                />
              </svg>
              <h4>{'AI-Powered'}</h4>
              <p>{'Advanced AI understands your intent and generates production-ready code'}</p>
            </div>
            <div className="feature-card">
              <svg className="feature-icon-small" viewBox="0 0 20 20">
                <path d="M16 10 A6 6 0 1 1 10 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <path d="M16 4 L16 10 L10 10" fill="currentColor"/>
              </svg>
              <h4>{'Legacy Modernization'}</h4>
              <p>{'Transform legacy systems into modern cloud-native applications'}</p>
            </div>
            <div className="feature-card">
              <svg className="feature-icon-small" viewBox="0 0 20 20">
                <path d="M10 2 L4 5 L4 10 C4 14 10 18 10 18 C10 18 16 14 16 10 L16 5 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                <path d="M8 10 L9.5 11.5 L12 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <h4>{'Enterprise Guardrails'}</h4>
              <p>{'Built-in security and compliance for enterprise deployments'}</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Getting Started',
      description: 'Sign in and start building with AI',
      icon: (
        <svg viewBox="0 0 24 24" className="step-icon" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      content: (
        <div className="step-content">
          <h3>{'1. Sign In'}</h3>
          <p>
            Start by signing in with your Mia Platform account. If you don't have an account yet, 
            you can create one in seconds.
          </p>
          <div className="demo-mockup">
            <div className="mockup-login">
              <div className="mockup-logo">
                <div className="logo-circle-small">
                  <svg viewBox="0 0 100 100">
                    <path
                      d="M50 10 L90 30 L90 70 L50 90 L10 70 L10 30 Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                    <circle cx="50" cy="50" fill="currentColor" r="15" />
                  </svg>
                </div>
                <h4>{'Mia Flow'}</h4>
              </div>
              <button className="mockup-button">
                {'Sign in with Mia Platform'}
              </button>
            </div>
          </div>
          <div className="tip-box">
            <svg viewBox="0 0 20 20" className="tip-icon">
              <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
              <path d="M10 6 L10 10" stroke="currentColor" strokeLinecap="round" strokeWidth="2"/>
              <circle cx="10" cy="13" fill="currentColor" r="1"/>
            </svg>
            <p>{'Tip: Your account gives you access to AI-powered development across all Mia Platform services'}</p>
          </div>
        </div>
      )
    },
    {
      title: 'Chat with AI',
      description: 'Natural language conversations that generate code',
      icon: (
        <svg viewBox="0 0 24 24" className="step-icon" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      content: (
        <div className="step-content">
          <h3>{'2. Start Chatting'}</h3>
          <p>
            Simply describe what you want to build in natural language. Mia Flow's AI will understand 
            your intent and generate the appropriate code, configurations, and deployments.
          </p>
          <div className="demo-mockup">
            <div className="mockup-chat">
              <div className="mockup-message user">
                <div className="message-avatar">{'👤'}</div>
                <div className="message-content">
                  {'Create a React component for a user profile card with avatar, name, and email'}
                </div>
              </div>
              <div className="mockup-message assistant">
                <div className="message-avatar">{'🤖'}</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="example-prompts">
            <h4>{'Example Prompts:'}</h4>
            <div className="prompt-list">
              <div className="prompt-item">{'💡 "Create a REST API for managing tasks"'}</div>
              <div className="prompt-item">{'💡 "Build a responsive dashboard with charts"'}</div>
              <div className="prompt-item">{'💡 "Generate a login form with validation"'}</div>
              <div className="prompt-item">{'💡 "Add a search feature to my app"'}</div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Canvas Mode',
      description: 'Visual editing and code preview',
      icon: (
        <svg viewBox="0 0 24 24" className="step-icon" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      content: (
        <div className="step-content">
          <h3>{'3. Visual Canvas'}</h3>
          <p>
            When AI generates code or documents, the Canvas automatically opens to show you a 
            beautiful preview. You can edit code, see live previews, and iterate with AI.
          </p>
          <div className="demo-mockup canvas-mockup">
            <div className="canvas-split">
              <div className="canvas-chat-side">
                <div className="canvas-label">{'Chat'}</div>
                <div className="mini-chat">
                  <div className="mini-message">{"AI: I've created your component..."}</div>
                </div>
              </div>
              <div className="canvas-preview-side">
                <div className="canvas-label">{'Canvas Preview'}</div>
                <div className="code-preview">
                  <div className="code-line">{'<div className="profile-card">'}</div>
                  <div className="code-line indent1">{'<img src={avatar} />'}</div>
                  <div className="code-line indent1">{'<h3>{name}</h3>'}</div>
                  <div className="code-line indent1">{'<p>{email}</p>'}</div>
                  <div className="code-line">{'</div>'}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="tip-box">
            <svg viewBox="0 0 20 20" className="tip-icon">
              <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
              <path d="M10 6 L10 10" stroke="currentColor" strokeLinecap="round" strokeWidth="2"/>
              <circle cx="10" cy="13" fill="currentColor" r="1"/>
            </svg>
            <p>{'Tip: You can edit the code directly in Canvas and ask AI to make further improvements'}</p>
          </div>
        </div>
      )
    },
    {
      title: 'Mia Platform Integration',
      description: 'Deploy directly to Mia Platform Console',
      icon: (
        <svg viewBox="0 0 24 24" className="step-icon" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      content: (
        <div className="step-content">
          <h3>{'4. Connect to Console'}</h3>
          <p>
            Connect Mia Flow to your Mia Platform Console to create projects, services, and deploy 
            directly from the chat interface. Mia Flow understands your Console structure and can 
            help you work with it.
          </p>
          <div className="integration-flow">
            <div className="flow-step">
              <div className="flow-icon">{'🔗'}</div>
              <h4>{'Connect'}</h4>
              <p>{'Link your Mia Platform account'}</p>
            </div>
            <div className="flow-arrow">{'→'}</div>
            <div className="flow-step">
              <div className="flow-icon">{'🎯'}</div>
              <h4>{'Select Project'}</h4>
              <p>{'Choose your target project'}</p>
            </div>
            <div className="flow-arrow">{'→'}</div>
            <div className="flow-step">
              <div className="flow-icon">{'🚀'}</div>
              <h4>{'Deploy'}</h4>
              <p>{'AI creates and deploys services'}</p>
            </div>
          </div>
          <div className="action-examples">
            <h4>{'What you can do:'}</h4>
            <ul>
              <li>{'✅ Create new microservices from templates'}</li>
              <li>{'✅ Configure endpoints and CRUD collections'}</li>
              <li>{'✅ Deploy services to environments'}</li>
              <li>{'✅ View logs and monitor deployments'}</li>
              <li>{'✅ Modernize existing services'}</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      title: 'AI Modes',
      description: 'Choose the right AI mode for your task',
      icon: (
        <svg viewBox="0 0 24 24" className="step-icon" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      content: (
        <div className="step-content">
          <h3>{'5. AI Modes'}</h3>
          <p>
            Mia Flow offers two AI modes to match your workflow. Switch between them based on 
            the complexity of your task.
          </p>
          <div className="modes-comparison">
            <div className="mode-card">
              <div className="mode-header agent">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h4>{'Agent Mode'}</h4>
              </div>
              <div className="mode-badge">{'Default'}</div>
              <p>{'Fast, direct responses for immediate results'}</p>
              <ul>
                <li>{'⚡ Quick code generation'}</li>
                <li>{'🎯 Direct answers'}</li>
                <li>{'💬 Conversational'}</li>
                <li>{'✨ Best for: Simple tasks, quick fixes, chatting'}</li>
              </ul>
            </div>
            <div className="mode-card">
              <div className="mode-header plan">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h4>{'Plan Mode'}</h4>
              </div>
              <div className="mode-badge advanced">{'Advanced'}</div>
              <p>{'Strategic thinking for complex projects'}</p>
              <ul>
                <li>{'🧠 Deep reasoning'}</li>
                <li>{'📋 Multi-step planning'}</li>
                <li>{'🔍 Problem analysis'}</li>
                <li>{'✨ Best for: Complex features, architecture, debugging'}</li>
              </ul>
            </div>
          </div>
          <div className="tip-box">
            <svg viewBox="0 0 20 20" className="tip-icon">
              <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
              <path d="M10 6 L10 10" stroke="currentColor" strokeLinecap="round" strokeWidth="2"/>
              <circle cx="10" cy="13" fill="currentColor" r="1"/>
            </svg>
            <p>{'Tip: Switch modes anytime using the dropdown in the chat interface'}</p>
          </div>
        </div>
      )
    },
    {
      title: 'Ready to Build',
      description: 'Start creating with Mia Flow today',
      icon: (
        <svg viewBox="0 0 24 24" className="step-icon" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      content: (
        <div className="step-content">
          <h3>{"You're All Set!"}</h3>
          <p>
            You now know the basics of Mia Flow. Ready to turn your ideas into reality? 
            Start a new chat and let AI help you build amazing things.
          </p>
          <div className="quick-start-cards">
            <div className="quick-card" onClick={() => navigate('/chat')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h4>{'Start New Chat'}</h4>
              <p>{'Begin a conversation with AI'}</p>
            </div>
            <div className="quick-card" onClick={() => navigate('/templates')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h4>{'Browse Templates'}</h4>
              <p>{'Start from a template'}</p>
            </div>
            <div className="quick-card" onClick={() => navigate('/tools')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h4>{'Connect Console'}</h4>
              <p>{'Link Mia Platform'}</p>
            </div>
          </div>
          <div className="resources-section">
            <h4>{'Additional Resources'}</h4>
            <div className="resource-links">
              <a href="https://docs.mia-platform.eu" target="_blank" rel="noopener noreferrer">
                {'📚 Documentation'}
              </a>
              <a href="https://www.mia-platform.eu/blog" target="_blank" rel="noopener noreferrer">
                {'📰 Blog & Tutorials'}
              </a>
              <a href="https://github.com/mia-platform" target="_blank" rel="noopener noreferrer">
                {'💻 GitHub'}
              </a>
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentStep = steps[activeStep];

  return (
    <div className="learn-page">
      {/* Header */}
      <div className="learn-header">
        <h1>{'Learn Mia Flow'}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Progress Indicator */}
      <div className="learn-progress">
        {steps.map((step, index) => (
          <div
            key={index}
            className={`progress-dot ${index === activeStep ? 'active' : ''} ${index < activeStep ? 'completed' : ''}`}
            onClick={() => setActiveStep(index)}
            title={step.title}
          >
            {index < activeStep ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            ) : (
              <span>{index + 1}</span>
            )}
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="learn-content">
        <div className="step-container">
          {/* Step Icon */}
          <div className="step-icon-container">
            {currentStep.icon}
          </div>

          {/* Step Title */}
          <h2 className="step-title">{currentStep.title}</h2>
          <p className="step-description">{currentStep.description}</p>

          {/* Step Content */}
          <div className="step-body">
            {currentStep.content}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="learn-navigation">
        <button
          className="nav-button prev"
          onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
          disabled={activeStep === 0}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {'Previous'}
        </button>

        <div className="step-counter">
          {activeStep + 1} {' of '} {steps.length}
        </div>

        {activeStep < steps.length - 1 ? (
          <button
            className="nav-button next"
            onClick={() => setActiveStep(Math.min(steps.length - 1, activeStep + 1))}
          >
            {'Next'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <button
            className="nav-button start"
            onClick={() => navigate('/chat')}
          >
            {'Start Building'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default Learn;
